import { useMemo } from 'react';
import type { Asset } from '../types/asset';

export interface ExpiryInfo {
  type: string;
  date: Date;
  isOverdue: boolean;
  isUpcoming: boolean;
  daysUntil: number;
}

export function useExpiry(item: Asset) {
  return useMemo(() => {
    const today = new Date();
    const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const checkExpiry = (date: string, periodicity: string): Date | null => {
      if (!date || !periodicity) return null;
      const lastDate = new Date(date);
      const months = parseInt(periodicity);
      if (isNaN(months)) return null;
      const expiryDate = new Date(lastDate);
      expiryDate.setMonth(expiryDate.getMonth() + months);
      return expiryDate;
    };

    const expiries: ExpiryInfo[] = [];

    const checks = [
      { name: 'Calibration', date: checkExpiry(item.calibrationDate, item.calibrationPeriodicity) },
      { name: 'Certification', date: checkExpiry(item.certificationDate, item.certificationPeriodicity) },
      { name: 'Test', date: checkExpiry(item.testDate, item.testPeriodicity) },
      { name: 'Audit', date: checkExpiry(item.auditDate, item.auditPeriodicity) },
    ];

    checks.forEach(check => {
      if (check.date) {
        const daysUntil = Math.ceil((check.date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        expiries.push({
          type: check.name,
          date: check.date,
          isOverdue: check.date < today,
          isUpcoming: check.date >= today && check.date <= thirtyDays,
          daysUntil,
        });
      }
    });

    const isOverdue = expiries.some(e => e.isOverdue);
    const isUpcoming = expiries.some(e => e.isUpcoming);
    const nextExpiry = expiries
      .filter(e => !e.isOverdue)
      .sort((a, b) => a.date.getTime() - b.date.getTime())[0];
    const overdueItems = expiries.filter(e => e.isOverdue);
    const upcomingItems = expiries.filter(e => e.isUpcoming);

    return {
      expiries,
      isOverdue,
      isUpcoming,
      nextExpiry,
      overdueItems,
      upcomingItems,
    };
  }, [item]);
}

export function useAssetStats(items: Asset[]) {
  return useMemo(() => {
    const today = new Date();
    const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const checkExpiry = (date: string, periodicity: string): Date | null => {
      if (!date || !periodicity) return null;
      const lastDate = new Date(date);
      const months = parseInt(periodicity);
      if (isNaN(months)) return null;
      const expiryDate = new Date(lastDate);
      expiryDate.setMonth(expiryDate.getMonth() + months);
      return expiryDate;
    };

    const isOverdue = (item: Asset) => {
      const checks = [
        checkExpiry(item.calibrationDate, item.calibrationPeriodicity),
        checkExpiry(item.certificationDate, item.certificationPeriodicity),
        checkExpiry(item.testDate, item.testPeriodicity),
        checkExpiry(item.auditDate, item.auditPeriodicity),
      ].filter((d): d is Date => d !== null);
      return checks.some(date => date < today);
    };

    const isUpcoming = (item: Asset) => {
      const checks = [
        checkExpiry(item.calibrationDate, item.calibrationPeriodicity),
        checkExpiry(item.certificationDate, item.certificationPeriodicity),
        checkExpiry(item.testDate, item.testPeriodicity),
        checkExpiry(item.auditDate, item.auditPeriodicity),
      ].filter((d): d is Date => d !== null);
      return checks.some(date => date >= today && date <= thirtyDays);
    };

    return {
      total: items.length,
      iso27001: items.filter(item => item.isISO27001).length,
      overdue: items.filter(item => isOverdue(item)).length,
      upcoming: items.filter(item => isUpcoming(item)).length,
      totalValue: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
    };
  }, [items]);
}